import {last, memoize} from 'lodash';
import polyline from '@mapbox/polyline';
import turfLength from '@turf/length';
import turfDistance from '@turf/distance';
import getChunkedStraightLine from './getChunkedStraightLine';
import getStraightLine from './getStraightLine';
import fetchJSON from '../../fetchJSON';
import convertLngLat from '../../mapbox/convertLngLat';

export interface FetchRoutedLineOptions {
    costing: Track['properties']['routing_mode'];
    fromLngLat: mapboxgl.LngLat;
    project: MapProject;
    toLngLat: mapboxgl.LngLat;
}

async function fetchRoutedLine({
    fromLngLat,
    toLngLat,
    project,
    costing,
}: FetchRoutedLineOptions): Promise<GeoJSON.LineString> {

    if (costing === 'point-to-point') {
        return getChunkedStraightLine(fromLngLat, toLngLat);
    }
    const url = `https://routing.gaiagps.com/route?json=${encodeURIComponent(JSON.stringify({
        locations: [
            {lon: fromLngLat.lng, lat: fromLngLat.lat, type: 'break'},
            {lon: toLngLat.lng, lat: toLngLat.lat, type: 'break'}
        ],
        costing
    }))}&max_hiking_difficulty=6`;

    let response: any;
    try {
        response = await fetchJSON(url);
    } catch (e) {
        return getStraightLine(fromLngLat, toLngLat);
    }

    if (response.status_code === 400) {
        return getChunkedStraightLine(fromLngLat, toLngLat);
    }

    const leg: Leg = response.trip.legs[0];
    const geometry = polyline.toGeoJSON(leg.shape, 6);
    const {coordinates} = geometry;

    const straightLineLength = turfDistance(fromLngLat.toArray(), toLngLat.toArray(), {
        units: 'meters'
    });
    const routedLineLength = turfLength({type: 'Feature', geometry, properties: {}}, {
        units: 'meters'
    });
    if (routedLineLength > straightLineLength * 10) {
        return getChunkedStraightLine(fromLngLat, toLngLat);
    }

    if (project) {
        const fromDist = project(convertLngLat(fromLngLat))
            .dist(project(convertLngLat(coordinates[0])));

        const toDist = project(convertLngLat(toLngLat))
            .dist(project(convertLngLat(last(coordinates)!)));

        const MAX_ENDPOINT_DEVIATION_PX = 20;
        if (fromDist >= MAX_ENDPOINT_DEVIATION_PX || toDist >= MAX_ENDPOINT_DEVIATION_PX) {
            return getChunkedStraightLine(fromLngLat, toLngLat);
        }
    }

    return geometry;
}

export default memoize(fetchRoutedLine, ({
    fromLngLat,
    toLngLat,
    costing
}: FetchRoutedLineOptions) => {
    return JSON.stringify([fromLngLat.toArray(), toLngLat.toArray(), costing]);
});

interface Leg {
    shape: string;
    maneuvers: {
        begin_shape_index: number;
        end_shape_index: number;
        instruction: string;
        length: number;
        time: number;
        travel_mode: string;
        travel_type: string;
        type: number;
        verbal_pre_transition_instruction: string;
    }[];
    summary: {
        length: number;
        max_lat: number;
        max_lon: number;
        min_lat: number;
        min_lon: number;
        time: number;
    };
}
